import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { listPasskeys, getPasskeyRegistrationOptions, verifyPasskeyRegistration, deletePasskey } from '../../lib/treeApi';
import { ScanFace, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

export function TreePasskeyManager({ open, onOpenChange }) {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const fetchPasskeys = async () => {
    setLoading(true);
    try { const { data } = await listPasskeys(); setPasskeys(data); } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { if (open) fetchPasskeys(); }, [open]);

  const handleAdd = async () => {
    setAdding(true);
    try {
      const { startRegistration } = await import('@simplewebauthn/browser');
      const { data: optsRaw } = await getPasskeyRegistrationOptions();
      const options = JSON.parse(optsRaw);
      const attestation = await startRegistration({ optionsJSON: options });
      await verifyPasskeyRegistration(attestation);
      toast.success('Face ID / Touch ID added for this device');
      fetchPasskeys();
    } catch (err) {
      if (err?.name === 'InvalidStateError') toast.error('This device is already added');
      else if (err?.name !== 'NotAllowedError') toast.error(err.response?.data?.detail || 'Could not add passkey');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (credentialId) => {
    try {
      await deletePasskey(credentialId);
      setPasskeys((prev) => prev.filter((p) => p.credential_id !== credentialId));
      toast.success('Removed');
    } catch { toast.error('Failed to remove'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="tree-passkey-manager-dialog">
        <DialogHeader><DialogTitle className="font-['Outfit'] flex items-center gap-2"><ScanFace className="w-5 h-5" /> Face ID / Touch ID</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <p className="text-xs text-[#4A5D53]">Add this device so you can sign back in with Face ID or Touch ID instead of typing your password.</p>
          {loading ? (
            <p className="text-sm text-[#4A5D53]">Loading…</p>
          ) : passkeys.length === 0 ? (
            <p className="text-sm text-[#4A5D53] italic" data-testid="tree-passkey-empty">No devices added yet</p>
          ) : (
            <div className="space-y-1.5">
              {passkeys.map((p) => (
                <div key={p.credential_id} className="flex items-center justify-between bg-[#F4F1ED] rounded-lg px-3 py-2" data-testid={`tree-passkey-row-${p.credential_id}`}>
                  <div>
                    <span className="text-sm text-[#0A1711] block">{p.device_type === 'multi_device' ? 'Synced device' : 'This device'}</span>
                    {p.created_at && <span className="text-[10px] text-[#4A5D53]">Added {new Date(p.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(p.credential_id)} className="text-red-500 hover:bg-red-50 h-7 px-2" data-testid={`tree-passkey-remove-${p.credential_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Button onClick={handleAdd} disabled={adding} className="w-full bg-[#1A4331] text-white" data-testid="tree-passkey-add-button">
            <Plus className="w-4 h-4 mr-1" /> {adding ? 'Waiting for device…' : 'Add this device'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
